import { formatDateString } from "@/v2-common/utils/utils.date"
import RelativeDate from "@/v2-backoffice-client/components/RelativeDate"
import BadgeList from "@/v2-backoffice-client/components/badgeList"
import { DotText } from "@/v2-backoffice-client/components/dot"
import TruncatedList from "@/v2-backoffice-client/components/truncatedList"
import {
  GROUPING_RANGE_DATES,
  type ColumnMeta,
  type ColumnMetaMap,
} from "@/v2-backoffice-client/components/table"
import LicenseGoverningBadge from "@/v2-backoffice-client/license/LicenseGoverningBadge"
import { getLicenseGoverningStatusLabel } from "@/v2-backoffice-client/license/license.utils"
import type { LicenseStatusFilterOption } from "@/v2-backoffice-client/license/license.statuses.hooks"
import { formatUserName } from "@/v2-backoffice-client/user/user.utils"
import UserActivityBadge from "@/v2-backoffice-client/user/activity/UserActivityBadge"
import { USER_ACTIVITY_LEVEL_FILTER_OPTIONS } from "@/v2-backoffice-client/user/activity/userActivity.constants"
import UserStatusBadge from "@/v2-backoffice-client/user/status/UserStatusBadge"
import { USER_STATUS_FILTER_OPTIONS } from "@/v2-backoffice-client/user/status/userStatus.constants"
import UserContactSyncCell from "@/v2-backoffice-client/user/contact/UserContactSyncCell"
import {
  CONTACT_SYNC_STATUS_CLIENT_FILTER_OPTIONS,
  getContactSyncStatusLabel,
} from "@/v2-backoffice-client/user/contact/userContact.constants"
import UserMigrationCell from "@/v2-backoffice-client/user/migration/UserMigrationCell"
import {
  ACTIVATION_STATUS_CLIENT_FILTER_OPTIONS,
  getActivationStatusLabel,
} from "@/v2-backoffice-client/tenant/migration/tenantMigration.constants"
import { buildUserUsageColumns } from "@/v2-backoffice-client/usage/usage.columns"
import type { UserFragment } from "@/v2-backoffice-client/user/__types__/user.fragment"

/**
 * Shared user-column builder for client-side tables whose rows carry a
 * UserFragment (as the row itself or nested, e.g. `row.User`): the standard
 * user columns every user-shaped table shows, in the standard order, with
 * the same badges, filters and grouping, so a user reads the same wherever
 * they are listed. Filters compare against each column's accessor value
 * (TanStack client-side filtering), which is why code-valued columns use the
 * code option lists and label-valued columns the `*_CLIENT_FILTER_OPTIONS`.
 *
 * Only the deep-link target differs per table (nested panel tables stack a
 * new sheet with `>>`), so the caller provides it, as for
 * buildUserUsageColumns.
 */

export type BuildUserColumnsInput<TData> = {
  /** Adapts the row to its user (identity, or e.g. `row.User`). */
  getUser(row: TData): UserFragment | null | undefined
  /** Deep link for the Name and Email cells: the user panel. */
  buildTo(row: TData): string | undefined
  /** Deep link for the usage cells: the user panel's Usage tab. */
  buildUsageTo(row: TData): string | undefined
  /**
   * Column ids to start hidden in this table, on top of the builder's own
   * defaults (Sign-up and Created). A table about something other than the
   * users themselves hides the CS columns (HubSpot, V2 User, Fusion Login).
   */
  defaultHidden?: string[]
}

export function buildUserColumns<TData>(
  input: BuildUserColumnsInput<TData>,
): ColumnMetaMap<TData> {
  const { getUser, buildTo, buildUsageTo, defaultHidden = [] } = input
  const to: ColumnMeta<TData>["to"] = ({ row }) => buildTo(row.original)

  const columns: ColumnMetaMap<TData> = {
    Name: {
      id: "Name",
      label: "Name",
      filter: { type: "text", placeholder: "Name..." },
      getValue: (row) => {
        const user = getUser(row)
        return formatUserName(user?.FirstName, user?.LastName)
      },
      to,
    },
    UserStatusCode: {
      id: "UserStatusCode",
      label: "Status",
      enableGrouping: true,
      filter: {
        type: "multi-select",
        placeholder: "Any status",
        options: USER_STATUS_FILTER_OPTIONS,
      },
      getValue: (row) => getUser(row)?.UserStatusCode,
      renderCell({ row }) {
        const user = getUser(row.original)
        if (!user) return null
        return <UserStatusBadge status={user.UserStatus} />
      },
      exportValue({ row }) {
        return getUser(row.original)?.UserStatus.Name ?? null
      },
    },
    UserActivityLevelCode: {
      id: "UserActivityLevelCode",
      label: "Activity",
      enableGrouping: true,
      filter: {
        type: "multi-select",
        placeholder: "Any activity",
        options: USER_ACTIVITY_LEVEL_FILTER_OPTIONS,
      },
      getValue: (row) => getUser(row)?.UserActivityLevelCode,
      renderCell({ row }) {
        const user = getUser(row.original)
        if (!user) return null
        return <UserActivityBadge level={user.UserActivityLevel} />
      },
      exportValue({ row }) {
        return getUser(row.original)?.UserActivityLevel.Name ?? null
      },
    },
    Email: {
      id: "Email",
      label: "Email",
      filter: { type: "text", placeholder: "Email..." },
      getValue: (row) => getUser(row)?.Email ?? "",
      to,
    },
    // HubSpot link state (the badge tooltip carries the "Sync to HubSpot"
    // repair action).
    ContactSyncStatus: {
      id: "ContactSyncStatus",
      label: "HubSpot",
      enableGrouping: true,
      filter: {
        type: "multi-select",
        placeholder: "Any HubSpot status",
        options: CONTACT_SYNC_STATUS_CLIENT_FILTER_OPTIONS,
      },
      getValue: (row) =>
        getContactSyncStatusLabel(getUser(row)?.ContactSyncStatus),
      renderCell({ row }) {
        const user = getUser(row.original)
        if (!user) return null
        return <UserContactSyncCell user={user} showHealthy />
      },
      exportValue({ row }) {
        return getContactSyncStatusLabel(
          getUser(row.original)?.ContactSyncStatus,
        )
      },
    },
    // The FUS-927 migration target group: who has actually used v2.
    UsesV2: {
      id: "UsesV2",
      label: "V2 User",
      enableGrouping: true,
      filter: {
        type: "select",
        placeholder: "V2 usage",
        options: [
          { label: "V2 user", value: "Yes" },
          { label: "Never used v2", value: "No" },
        ],
      },
      getValue: (row) => (getUser(row)?.UsesV2 ? "Yes" : "No"),
      renderCell({ row }) {
        const user = getUser(row.original)
        if (!user) return null
        return (
          <DotText variant={user.UsesV2 ? "success-1" : "muted"}>
            {user.UsesV2 ? "Yes" : "No"}
          </DotText>
        )
      },
      exportValue({ row }) {
        return getUser(row.original)?.UsesV2 ? "Yes" : "No"
      },
    },
    // Fusion-login migration state (FUS-927 CS-managed waves).
    MigrationStatus: {
      id: "MigrationStatus",
      label: "Fusion Login",
      enableGrouping: true,
      filter: {
        type: "multi-select",
        placeholder: "Any migration status",
        options: ACTIVATION_STATUS_CLIENT_FILTER_OPTIONS,
      },
      getValue: (row) =>
        getActivationStatusLabel(getUser(row)?.MigrationStatus),
      renderCell({ row }) {
        const user = getUser(row.original)
        if (!user?.MigrationStatus) return null
        return <UserMigrationCell user={user} />
      },
      exportValue({ row }) {
        return getActivationStatusLabel(getUser(row.original)?.MigrationStatus)
      },
    },
    TenantNames: {
      id: "TenantNames",
      label: "Team(s)",
      enableGrouping: true,
      // First team as the value: one group per team a person leads with,
      // as on the other user tables; the cell lists them all.
      filter: { type: "text", placeholder: "Team name..." },
      getValue: (row) => getUser(row)?.TenantNames?.[0] ?? "",
      renderCell({ row }) {
        const user = getUser(row.original)
        if (!user) return null
        return (
          <TruncatedList
            items={user.Tenants.map((tenant) => ({
              label: tenant.Name,
              to: `>>?v=tenant:${tenant.Id}`,
            }))}
          />
        )
      },
      exportValue({ row }) {
        return getUser(row.original)?.TenantNames?.join(", ") ?? ""
      },
    },
    ...buildUserUsageColumns<TData>({
      getUsage: (row) => getUser(row),
      buildTo: buildUsageTo,
    }),
    LastSignInAt: {
      id: "LastSignInAt",
      label: "Last Sign-in",
      filter: { type: "date-filter" },
      groupingRanges: GROUPING_RANGE_DATES,
      getValue: (row) => getUser(row)?.LastSignInAt,
      renderCell({ row }) {
        return <RelativeDate date={getUser(row.original)?.LastSignInAt} />
      },
      exportValue({ row }) {
        return formatDateString(getUser(row.original)?.LastSignInAt)
      },
    },
    IdentityCreatedAt: {
      id: "IdentityCreatedAt",
      label: "Sign-up",
      defaultHidden: true,
      filter: { type: "date-filter" },
      groupingRanges: GROUPING_RANGE_DATES,
      getValue: (row) => getUser(row)?.IdentityCreatedAt,
      renderCell({ row }) {
        return <RelativeDate date={getUser(row.original)?.IdentityCreatedAt} />
      },
      exportValue({ row }) {
        return formatDateString(getUser(row.original)?.IdentityCreatedAt)
      },
    },
    CreatedAt: {
      id: "CreatedAt",
      label: "Created",
      defaultHidden: true,
      filter: { type: "date-filter" },
      groupingRanges: GROUPING_RANGE_DATES,
      getValue: (row) => getUser(row)?.CreatedAt,
      renderCell({ row }) {
        return <RelativeDate date={getUser(row.original)?.CreatedAt} />
      },
      exportValue({ row }) {
        return formatDateString(getUser(row.original)?.CreatedAt)
      },
    },
  }

  for (const id of defaultHidden) {
    const column = columns[id]
    if (column) columns[id] = { ...column, defaultHidden: true }
  }

  return columns
}

/** The license posture a user row carries: the owning customer's, from the full-User procs. */
type UserLicensePosture = Pick<
  UserFragment,
  "LicenseStatusCode" | "LicenseTrialEndsAt" | "LicensePlanNames"
>

export type BuildUserLicenseColumnsInput<TData> = {
  /** Adapts the row to its user (identity, or e.g. `row.User`). */
  getUser(row: TData): UserLicensePosture | null | undefined
  /** Status display names from the getLicenseStatuses lookup. */
  statusNamesByCode: Map<number, string>
  /**
   * The License status filter's options: the codes on a server-filtered
   * board, the labels on a client-side table (useLicenseGoverningStatuses
   * has both).
   */
  statusOptions: LicenseStatusFilterOption[]
  /**
   * A server-paged board: the filters name the operators usersFilterRegistry
   * compiles, and the aggregated Licenses string offers no sort (the proc
   * has no expression for it). A client-side table sorts and filters
   * against getValue.
   */
  isServerFiltered?: boolean
}

/**
 * The three license columns of a user row (License status, Licenses, Trial
 * Ends), the same on every user-shaped table: the posture of the customer
 * that owns the user, which is what decides what they can use. A limited
 * seated license is listed under Licenses for everyone at the customer; who
 * actually holds a seat on it is the Seats column (buildUserSeatsColumn).
 */
export function buildUserLicenseColumns<TData>(
  input: BuildUserLicenseColumnsInput<TData>,
): ColumnMetaMap<TData> {
  const { getUser, statusNamesByCode, statusOptions, isServerFiltered } = input

  function statusLabel(row: TData) {
    return getLicenseGoverningStatusLabel(
      getUser(row)?.LicenseStatusCode,
      statusNamesByCode,
    )
  }

  const statusFilter: ColumnMeta<TData>["filter"] = {
    type: "multi-select",
    placeholder: "Any license status",
    options: statusOptions,
    ...(isServerFiltered ? { operators: ["EQ", "NEQ", "IN"] } : {}),
  }
  const planNamesFilter: ColumnMeta<TData>["filter"] = {
    type: "text",
    placeholder: "License or product...",
    ...(isServerFiltered ? { operators: ["CONTAINS", "EQ"] } : {}),
  }

  return {
    LicenseStatusCode: {
      id: "LicenseStatusCode",
      label: "License status",
      description:
        "The governing license status of the customer that owns the user: a trial outranks everything, then Active, Not renewing, Scheduled, Expired, Not starting. No license = the customer holds none; blank = nobody owns the user, so no license applies.",
      defaultSize: 150,
      enableGrouping: true,
      filter: statusFilter,
      getValue: statusLabel,
      renderCell({ row }) {
        const user = getUser(row.original)
        if (!user) return null
        return (
          <LicenseGoverningBadge
            license={user}
            statusName={
              user.LicenseStatusCode != null
                ? statusNamesByCode.get(user.LicenseStatusCode)
                : undefined
            }
          />
        )
      },
      exportValue({ row }) {
        return statusLabel(row.original)
      },
    },
    LicensePlanNames: {
      id: "LicensePlanNames",
      label: "Licenses",
      description:
        "The licenses the owning customer holds: the plan tier, or the product for a plan-less grant or trial. A seated license limited to named people reaches only its seat holders, so read it with the Seats column.",
      // Room for one full product name beside the "+N" pill.
      defaultSize: 200,
      enableGrouping: true,
      enableSorting: !isServerFiltered,
      filter: planNamesFilter,
      getValue: (row) => getUser(row)?.LicensePlanNames ?? "",
      // The procs hand the names over as one ", "-joined string; split for
      // the pills, so a customer holding three reads as one pill and "+2"
      // rather than a cut-off line. Two licenses on the same plan repeat
      // the name, hence the positional key.
      renderCell({ row }) {
        const names = getUser(row.original)?.LicensePlanNames
        if (!names) return null
        return (
          <BadgeList
            items={names.split(", ").map((name, index) => ({
              key: `${index}:${name}`,
              label: name,
            }))}
            overflowTitle="Licenses"
          />
        )
      },
      exportValue({ row }) {
        return getUser(row.original)?.LicensePlanNames ?? ""
      },
    },
    // The trial clock behind the License status countdown, as a date
    // column of its own so sales can filter "trials ending this week".
    LicenseTrialEndsAt: {
      id: "LicenseTrialEndsAt",
      label: "Trial Ends",
      description:
        "The soonest trial end among the owning customer's trial licenses, the clock behind the License status countdown.",
      defaultSize: 110,
      defaultHidden: true,
      filter: { type: "date-filter" },
      groupingRanges: GROUPING_RANGE_DATES,
      getValue: (row) => getUser(row)?.LicenseTrialEndsAt,
      renderCell({ row }) {
        const value = getUser(row.original)?.LicenseTrialEndsAt
        return value ? formatDateString(value) : null
      },
      exportValue({ row }) {
        return formatDateString(getUser(row.original)?.LicenseTrialEndsAt)
      },
    },
  }
}

export type BuildUserSeatsColumnInput<TData> = {
  getUserId(row: TData): number | null | undefined
  /** The seats each person holds, as product names (groupSeatProductsByUser). */
  seatProductsByUserId: Map<number, string[]>
}

/**
 * The Seats column of a user row: the seats this person holds on their
 * customer's seated licenses (the MCP servers), by product. Blank means no
 * seat, which on a license limited to named people means the license does
 * not reach them. The holders come from one customer-wide read
 * (getLicenseSeatHolders), so a table lists it for the customers it has
 * read; the top-level Users board, which spans every customer, has no such
 * read yet.
 */
export function buildUserSeatsColumn<TData>(
  input: BuildUserSeatsColumnInput<TData>,
): ColumnMetaMap<TData> {
  const { getUserId, seatProductsByUserId } = input

  function productsOf(row: TData) {
    const userId = getUserId(row)
    return userId == null ? [] : (seatProductsByUserId.get(userId) ?? [])
  }

  return {
    Seats: {
      id: "Seats",
      label: "Seats",
      description:
        "Seats this person holds on the customer's seated licenses (the MCP servers). A seat is what lets them use a license limited to named people; blank means they hold none. Named on the customer panel's Licenses & add-ons tab (row menu, Seats).",
      defaultSize: 170,
      enableGrouping: true,
      filter: { type: "text", placeholder: "Seat on..." },
      getValue: (row) => productsOf(row).join(", "),
      renderCell({ row }) {
        const products = productsOf(row.original)
        if (products.length === 0) return null
        return (
          <BadgeList
            items={products.map((product) => ({ label: product }))}
            overflowTitle="Seats"
          />
        )
      },
      exportValue({ row }) {
        return productsOf(row.original).join(", ")
      },
    },
  }
}
